import { AfterViewInit, Component, OnInit, QueryList, ViewChildren, OnChanges } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';

// Angular Material
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSort, MatSortModule } from '@angular/material/sort';

// Shared
import { BreadcrumbComponent } from '@app/lib/components/breadcrumb/breadcrumb.component';
import { BaseHttpService } from '@app/lib/services/base-http.service';
import { AppConfiguration } from '@app/lib/services/app-config.service';
import { DialogService } from '@app/lib/services/dialog.service';
import { ConfirmationDialogComponent } from '@app/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-session-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDatepickerModule,
    BreadcrumbComponent,
    MatProgressBarModule,
    MatDialogModule,
    MatSortModule
  ],
  providers: [DatePipe],
  templateUrl: './session-details.component.html',
  styleUrl: './session-details.component.scss'
})
export class SessionDetailsComponent extends BaseHttpService implements OnInit, AfterViewInit {
  
  public config: any = {
    pageTitle: 'Session Details',
    breadcrumbs: [{ label: 'Court', url: '/' }, { label: 'Session Details' }],
    searchFields: [
        { key: 'name', label: 'Session Name', type: 'text' },
        { key: 'beginDate', label: 'Session Date', type: 'date' },
    ],
    masterTable: {
        panelTitle: 'Session Details',
        detailDataKey: 'panelData', // Based on config provided
        columns: [
            { key: 'name', label: 'Name', type: 'select', isEditable: true, required: true, endpoint: 'mtSession-master/get/lov.htm',
              endpointMethod: 'GET', endpointValueKey: 'id', endpointLabelKey: 'name', options: [] as any[] },
            { key: 'beginDate', label: 'Begin Date', type: 'date', isEditable: true, required: true },
            { key: 'endDate', label: 'End Date', type: 'date', isEditable: true, required: true },
        ],
        actions: {
            showAdd: true,
            showSave: true,
            showDelete: true
        }
    },
    detailTable: {
        panelTitle: 'Panel',
        columns: [
            { key: 'name', label: 'Name', type: 'select', isEditable: false, endpoint: 'justicePanel-assign/get/lov.htm',
              endpointMethod: 'GET', endpointValueKey: 'id', endpointLabelKey: 'name', options: [] as any[] },
            { key: 'activeStartDate', label: 'Active Start Date', type: 'date', isEditable: true, required: true },
            { key: 'activeEndDate', label: 'Active End Date', type: 'date', isEditable: true, required: true },
        ],
        actions: {
            showAdd: true,
        }
    }
  };

  searchForm!: FormGroup;
  isLoading = false;
  
  // Master Table
  masterDataSource = new MatTableDataSource<any>();
  masterDisplayedColumns = ['select', 'name', 'beginDate', 'endDate'];
  selectedMasterRows: any[] = [];
  singleSelectedMasterRow: any = null;
  isMasterAddMode = false;
  isMasterEditMode = false;

  // Detail Table
  detailDataSource = new MatTableDataSource<any>();
  detailDisplayedColumns = ['select', 'name', 'activeStartDate', 'activeEndDate'];
  selectedDetailRows: any[] = [];
  isDetailAddMode = false;

  @ViewChildren(MatSort) sorts!: QueryList<MatSort>;

  constructor(
    private fb: FormBuilder,
    protected httpClient: HttpClient,
    appConfig: AppConfiguration,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private datePipe: DatePipe
  ) {
    super(httpClient, appConfig);
  }

  ngOnInit(): void {
    this.initSearchForm();
    this.fetchDropdownOptions();
  }

  ngAfterViewInit() {
    this.sorts.changes.subscribe(() => this.applySorting());
    this.applySorting();
  }

  applySorting() {
    const sortArray = this.sorts.toArray();
    if (sortArray[0]) {
      this.masterDataSource.sort = sortArray[0];
      this.masterDataSource.sortingDataAccessor = (item, property) => {
        if (property === 'beginDate' || property === 'endDate') {
          return this.parseDate(item[property])?.getTime() || 0;
        }
        return item[property];
      };
    }
    if (sortArray[1]) {
      this.detailDataSource.sort = sortArray[1];
      this.detailDataSource.sortingDataAccessor = (item, property) => {
        if (property === 'activeStartDate' || property === 'activeEndDate') {
          return this.parseDate(item[property])?.getTime() || 0;
        }
        return item[property];
      };
    }
  }

  public parseDate(dateStr: any): Date | null {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    
    // Handle DD-MM-YYYY format specifically
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) {
        return new Date(+parts[2], +parts[1] - 1, +parts[0]);
      }
    }
    
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }

  onDropdownSelectionChange(element: any, options: any[], value: any, type: 'master' | 'detail') {
    const selectedOpt = options.find(opt => opt.value === value);
    if (selectedOpt) {
      if (type === 'master') {
        element.sessionId = selectedOpt.value;
        element.name = selectedOpt.label;
      } else {
        element.id = selectedOpt.value;
        element.name = selectedOpt.label;
      }
    }
  }

  getMinBeginDate(element: any): Date | null {
    if (!element._isNew) return null;
    const validEndDates = this.masterDataSource.data
      .filter(row => row !== element)
      .map(row => this.parseDate(row.endDate))
      .filter(d => d !== null) as Date[];

    if (validEndDates.length === 0) return null;
    const maxEndDate = new Date(Math.max(...validEndDates.map(d => d.getTime())));
    const minDate = new Date(maxEndDate);
    minDate.setDate(minDate.getDate() + 1);
    return minDate;
  }

  getMinEndDate(beginDate: any): Date | null {
    const d = this.parseDate(beginDate);
    if (!d) return null;
    const minDate = new Date(d);
    minDate.setDate(minDate.getDate() + 1);
    return minDate;
  }

  initSearchForm(): void {
    this.searchForm = this.fb.group({
      name: [''],
      beginDate: ['']
    });
  }

  fetchDropdownOptions(): void {
    // Master Table Dropdowns
    const nameCol = this.config.masterTable.columns.find((c: any) => c.key === 'name');
    if (nameCol && nameCol.endpoint) this.loadOptions(nameCol);

    // Detail Table Dropdowns
    const panelCol = this.config.detailTable.columns.find((c: any) => c.key === 'name');
    if (panelCol && panelCol.endpoint) this.loadOptions(panelCol);
  }

  private loadOptions(field: any): void {
    const method = field.endpointMethod || 'GET';
    const req = method === 'GET' ? this.get<any>(field.endpoint) : this.post<any, any>(field.endpoint, {});
    req.subscribe({
      next: (res: any) => {
        const dataArray = res?.success || res?.data || res || [];
        if (Array.isArray(dataArray)) {
          field.options = dataArray.map((item: any) => ({
            value: field.endpointValueKey ? item[field.endpointValueKey] : (item.id || item.value),
            label: item.name || item.label || item[field.endpointLabelKey]
          }));
        }
      }
    });
  }

  handleSearch(): void {
    const formValue = this.searchForm.value;
    // Process search as per payloadBuilder
    const payload = { ...formValue };
    Object.keys(payload).forEach(key => {
      if (payload[key] === null) payload[key] = '';
      if (key === 'beginDate' && payload[key]) {
        payload[key] = this.formatDateToDDMMYYYY(payload[key]);
      }
    });

    this.isLoading = true;
    this.get<any>('ttSession-details/search.htm', { params: payload }).subscribe({
      next: (res: any) => {
        this.masterDataSource.data = res?.success || res?.data || res || [];
        this.resetState();
        if (this.masterDataSource.data.length === 0) {
          this.showDialog('Information', 'No Data Exist');
        }
      },
      error: () => this.isLoading = false
    });
  }

  handleReset(): void {
    this.searchForm.reset();
    this.masterDataSource.data = [];
    this.resetState();
  }

  resetState(): void {
    this.isLoading = false;
    this.isMasterAddMode = false;
    this.isMasterEditMode = false;
    this.selectedMasterRows = [];
    this.singleSelectedMasterRow = null;
    this.detailDataSource.data = [];
    this.selectedDetailRows = [];
    this.isDetailAddMode = false;
  }

  // --- Master Table ---
  onMasterRowClick(row: any): void {
    if (this.isMasterAddMode || this.isMasterEditMode) return;
    this.singleSelectedMasterRow = row;
    this.detailDataSource.data = row.panelData || [];
    this.selectedDetailRows = [];
  }

  toggleMasterSelection(row: any, checked: boolean): void {
    if (checked) {
      this.selectedMasterRows = [row];
      this.onMasterRowClick(row);
    } else {
      this.selectedMasterRows = [];
      this.singleSelectedMasterRow = null;
      this.detailDataSource.data = [];
    }
  }

  handleMasterAdd(): void {
    this.isMasterAddMode = true;
    const newRow: any = { 
      _isNew: true, 
      _isEdit: true, 
      panelData: [],
      sessionId: null,
      name: '',
      beginDate: null,
      endDate: null
    };
    
    this.masterDataSource.data = [newRow, ...this.masterDataSource.data];
    this.singleSelectedMasterRow = newRow;
    this.detailDataSource.data = [];
  }

  handleMasterDelete(): void {
    if (this.selectedMasterRows.length === 0) return;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { title: 'Confirm Action', message: 'Are you sure you want to delete?' }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) this.executeMasterDelete();
    });
  }

   private executeMasterDelete(): void {
    const payload = this.selectedMasterRows.map(record => {
      const bDate = this.parseDate(record.beginDate);
      const eDate = this.parseDate(record.endDate);
      return {
        id: bDate?.getTime() || record.id,
        sessionId: record.sessionId,
        description: record.description || "",
        beginDate: this.datePipe.transform(bDate, "yyyy-MM-dd'T'HH:mm:ss"),
        endDate: this.datePipe.transform(eDate, "yyyy-MM-dd'T'HH:mm:ss"),
        transType: "",
        name: record.name,
        panelId: record.panelId || record.sessionId
      };
    });

    this.isLoading = true;
    this.post('ttSession-details/delete.htm', payload).subscribe({
      next: (res: any) => {
        this.showDialog('Success', res.message || 'Deleted successfully');
        this.handleSearch();
      },
      error: () => this.isLoading = false
    });
  }

  handleMasterSave(): void {
    if (!this.isMasterAddMode && !this.isMasterEditMode && !this.isDetailAddMode) return;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { title: 'Confirm Action', message: 'Are you sure you want to save?' }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) this.executeMasterSave();
    });
  }

  private executeMasterSave(): void {
    // 1. Validate
    if (!this.validateData()) return;

    // 2. Prep payload
    const formattedPayload = this.masterDataSource.data
      .filter(r => r._isNew || r._isEdit)
      .map(row => {
        const panelData = (this.singleSelectedMasterRow === row ? this.detailDataSource.data : (row.panelData || []))
          .map((p: any) => ({
            id: p.id || 0,
            name: p.name,
            activeStartDate: this.datePipe.transform(this.parseDate(p.activeStartDate), "yyyy-MM-dd'T'HH:mm:ss"),
            activeEndDate: this.datePipe.transform(this.parseDate(p.activeEndDate), "yyyy-MM-dd'T'HH:mm:ss"),
            transType: p._isNew ? 'I' : 'U'
          }));

        return {
          sessionId: row.sessionId || row.id || 0,
          description: row.description || '',
          beginDate: this.datePipe.transform(this.parseDate(row.beginDate), "yyyy-MM-dd'T'HH:mm:ss"),
          endDate: this.datePipe.transform(this.parseDate(row.endDate), "yyyy-MM-dd'T'HH:mm:ss"),
          transType: row._isNew ? 'I' : 'U',
          name: row.name,
          panelData: panelData
        };
      });

    this.isLoading = true;
    this.post('ttSession-details/upsert.htm', formattedPayload).subscribe({
      next: (res: any) => {
        this.showDialog('Success', res.message || 'Saved successfully');
        this.handleSearch();
      },
      error: () => this.isLoading = false
    });
  }

  private validateData(): boolean {
    // Simple validation for required fields
    for (const row of this.masterDataSource.data) {
        if (row._isNew || row._isEdit) {
            if (!row.name || !row.beginDate || !row.endDate) {
                this.showDialog('Validation Error', 'All Master fields are required.');
                return false;
            }
            if (this.parseDate(row.beginDate)! >= this.parseDate(row.endDate)!) {
                this.showDialog('Validation Error', 'End Date must be after Begin Date.');
                return false;
            }
            if (this.detailDataSource.data.length === 0) {
                this.showDialog('Validation Error', 'Please add at least one Panel.');
                return false;
            }
            for (const p of this.detailDataSource.data) {
                if (!p.activeStartDate || !p.activeEndDate) {
                    this.showDialog('Validation Error', 'All Panel dates are required.');
                    return false;
                }
                if (this.parseDate(p.activeStartDate)! >= this.parseDate(p.activeEndDate)!) {
                  this.showDialog('Validation Error', 'Panel End Date must be after Start Date.');
                  return false;
                }
            }
        }
    }
    return true;
  }

  private cleanRecord(row: any) {
    const cleaned = { ...row };
    delete cleaned._isNew;
    delete cleaned._isEdit;
    if (this.singleSelectedMasterRow === row) {
        cleaned.panelData = this.detailDataSource.data;
    }
    return cleaned;
  }

  handleMasterCancel(): void {
    this.handleReset();
  }

  // --- Detail Table ---
  handleDetailAdd(): void {
    if (!this.singleSelectedMasterRow) return;
    this.isDetailAddMode = true;
    const newRow = { _isNew: true, _isEdit: true, id: null, name: '', activeStartDate: null, activeEndDate: null };
    this.detailDataSource.data = [newRow, ...this.detailDataSource.data];
  }

  private formatDateToDDMMYYYY(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  private showDialog(title: string, message: string): void {
    this.dialogService.openDialog1({
      data: { title, message, icon: title.toLowerCase().includes('error') ? 'error' : 'info' }
    });
  }
}
